import React, { useMemo } from 'react';

const TreatmentPlanReviewAnalytics = ({ chartData }) => {
  const analytics = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const total = chartData.length;
    const overdue = chartData.filter(d => d.status === 'Overdue');
    const dueSoon = chartData.filter(d => d.status === 'Due Soon');
    const onTime = chartData.filter(d => d.status === 'On Time');

    const overduePct = (overdue.length / total * 100);
    const dueSoonPct = (dueSoon.length / total * 100);

    const complianceScore = (onTime.length + dueSoon.length) / total * 100;
    const complianceRating = complianceScore >= 90 ? 'Excellent' : complianceScore >= 75 ? 'Good' : 'Needs Attention';

    const avgInterval = chartData.reduce((sum, d) => sum + d.targetReviewInterval, 0) / total;

    const mostOverdue = overdue.length > 0
      ? chartData.reduce((min, d) => d.daysUntilDue < min.daysUntilDue ? d : min, chartData[0])
      : null;

    return {
      total,
      overdue,
      dueSoon,
      overduePct,
      dueSoonPct,
      complianceScore,
      complianceRating,
      avgInterval,
      mostOverdue
    };
  }, [chartData]);

  if (!analytics) {
    return null;
  }

  const ratingColor = analytics.complianceRating === 'Excellent'
    ? 'text-green-600'
    : analytics.complianceRating === 'Good'
    ? 'text-blue-600'
    : 'text-red-600';

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Treatment Plan Review Tracker Analysis</h3>

      {/* Compliance Status Overview */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-blue-900 mb-3">Compliance Status Overview</h4>
        <ul className="space-y-2 text-gray-800">
          <li><span className="font-semibold">Total Clients:</span> {analytics.total}</li>
          <li>
            <span className="font-semibold">Overdue Reviews:</span>{' '}
            <span className="text-red-600 font-medium">{analytics.overdue.length} ({analytics.overduePct.toFixed(1)}%)</span>
          </li>
          <li>
            <span className="font-semibold">Due Soon (Next 14 Days):</span>{' '}
            <span className="text-orange-500 font-medium">{analytics.dueSoon.length} ({analytics.dueSoonPct.toFixed(1)}%)</span>
          </li>
          <li>
            <span className="font-semibold">Overall Compliance Score:</span>{' '}
            <span className={`font-medium ${ratingColor}`}>{analytics.complianceScore.toFixed(1)}%</span>
          </li>
          <li>
            <span className="font-semibold">Compliance Rating:</span>{' '}
            <span className={`font-medium ${ratingColor}`}>{analytics.complianceRating}</span>
          </li>
          <li>
            <span className="font-semibold">Average Review Interval:</span>{' '}
            <span className="font-medium">{analytics.avgInterval.toFixed(0)} days</span>
          </li>
        </ul>
      </div>

      {/* Action Items */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-blue-900 mb-3">Action Items</h4>

        <p className="font-semibold text-gray-800 mb-1">Overdue:</p>
        {analytics.overdue.length > 0 ? (
          <ul className="list-disc list-inside space-y-1 text-gray-700 mb-4">
            {analytics.overdue.map(d => (
              <li key={d.clientId}>
                <span className="font-medium">{d.clientId}:</span> {Math.abs(d.daysUntilDue)} days overdue
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 mb-4 ml-4">No overdue reviews</p>
        )}

        <p className="font-semibold text-gray-800 mb-1">Due within 14 days:</p>
        {analytics.dueSoon.length > 0 ? (
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {analytics.dueSoon.map(d => (
              <li key={d.clientId}>
                <span className="font-medium">{d.clientId}:</span> due in {d.daysUntilDue} days (on {d.nextReviewDue})
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 ml-4">No reviews due within 14 days</p>
        )}
      </div>

      {/* Review Status Summary */}
      <div>
        <h4 className="text-lg font-semibold text-blue-900 mb-3">Review Status Summary</h4>
        {analytics.mostOverdue && analytics.overdue.length > 0 ? (
          <p className="text-gray-800">
            <span className="font-semibold">Most Overdue Client:</span>{' '}
            <span className="text-red-600 font-medium">
              {analytics.mostOverdue.clientId} ({Math.abs(analytics.mostOverdue.daysUntilDue)} days overdue)
            </span>
          </p>
        ) : (
          <p className="text-green-600 font-medium">No overdue reviews.</p>
        )}
      </div>
    </div>
  );
};

export default TreatmentPlanReviewAnalytics;
